import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Chip, Stack, Button, Divider, TextField, Alert, Collapse, Switch, FormControlLabel } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import MfaSetup from "../components/MfaSetup";
import PricingCalculator from "../components/PricingCalculator";
import { OverviewSection, InvoicesSection, Dac7Section, UsersSection, AuditSection, AdminOverviewData } from "../components/admin/AdminSections";
import AdminAutomation from "./admin/AdminAutomation";
import AdminApi, { AdminPsychologist, AdminOrg, AdminDoc, VerificationState } from "../api/AdminApi";


// Compliance-jaarkalender: terugkerende wettelijke acties (DAC7-rondes,
// mutualiteiten-hercheck, BTW-review) met status + afvinken per cyclus.
// De mail-herinnering loopt via de settlement-cron; dit blok is de waarheid.

// Settlement-waakhond + reconciliatie: de maandelijkse cron verplaatst echt
// geld — dit blok toont het run-journaal en de maandcontrole
// (factuur-pass-through ↔ usage-snapshots ↔ payouts; verschil hoort €0).
function BillingWatchdog() {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<Awaited<ReturnType<typeof AdminApi.settlementRuns>>["runs"]>([]);
  const [recon, setRecon] = useState<Awaited<ReturnType<typeof AdminApi.reconciliation>> | null>(null);
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  useEffect(() => {
    AdminApi.settlementRuns().then((r) => setRuns(r.runs)).catch(() => setRuns([]));
    AdminApi.reconciliation(prev.getFullYear(), prev.getMonth() + 1).then(setRecon).catch(() => setRecon(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const eurC = (c: number) => `€${(c / 100).toFixed(2)}`;
  return (
    <Box>
      <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 1.5, display: "none" }}>{t("admin.watchdogTitle")}</Typography>
      {runs.length === 0 ? (
        <Typography sx={{ fontSize: ".9rem", color: "text.secondary" }}>{t("admin.watchdogNoRuns")}</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0} sx={{ mb: 2 }}>
          {runs.slice(0, 4).map((r, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75, flexWrap: "wrap" }}>
              <Chip size="small" label={r.ok ? "OK" : t("admin.watchdogAttention")} color={r.ok ? "success" : "error"} />
              <Typography sx={{ fontSize: ".88rem", m: 0 }}>
                {r.year}-{String(r.month).padStart(2, "0")} · {new Date(r.ranAt).toLocaleString()} — {r.payoutsCount} payouts ({eurC(r.payoutCents)}), {r.employerInvoicesCreated + r.providerInvoicesCreated} facturen, {r.overdueCount} overdue
                {r.payoutsPendingCount > 0 && <Box component="span" sx={{ color: "error.main" }}> · {r.payoutsPendingCount} payout(s) NIET uitgevoerd</Box>}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      {recon && recon.rows.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 600, fontSize: ".92rem", mb: 0.5 }}>{t("admin.reconTitle", { month: `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}` })}</Typography>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem", "& td, & th": { textAlign: "left", py: 0.5, pr: 2, borderBottom: "1px solid var(--border)" } }}>
            <thead><tr><th>{t("admin.reconOrg")}</th><th>{t("admin.reconInvoiced")}</th><th>{t("admin.reconUsage")}</th><th>{t("admin.reconDiff")}</th><th>{t("admin.reconOpen")}</th></tr></thead>
            <tbody>
              {recon.rows.map((row, i) => (
                <tr key={i}>
                  <td>{row.orgName}{row.invoiceNumber ? ` (${row.invoiceNumber})` : ""}</td>
                  <td>{eurC(row.invoicedPassThroughCents)}</td>
                  <td>{eurC(row.usagePassThroughCents)}</td>
                  <td style={{ color: row.diffCents === 0 ? "inherit" : "#c62828", fontWeight: row.diffCents === 0 ? 400 : 700 }}>{eurC(row.diffCents)}</td>
                  <td>{row.unpaidPayouts > 0 || row.unsettledUsage > 0 ? `${row.unpaidPayouts} payout / ${row.unsettledUsage} usage` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </>
      )}
    </Box>
  );
}

function ComplianceCalendar() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Awaited<ReturnType<typeof AdminApi.complianceCalendar>>["items"]>([]);
  const load = useCallback(() => { AdminApi.complianceCalendar().then((r) => setItems(r.items)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  if (items.length === 0) return null;
  const color: Record<string, "default" | "success" | "warning" | "error"> = { upcoming: "default", open: "warning", overdue: "error", done: "success" };
  return (
    <Box>
      <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 0.5, display: "none" }}>{t("admin.complianceTitle")}</Typography>
      <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mb: 1.5 }}>{t("admin.complianceHint")}</Typography>
      <Stack divider={<Divider />} spacing={0}>
        {items.map((it) => (
          <Box key={it.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, flexWrap: "wrap" }}>
            <Chip size="small" label={t(`admin.complianceState.${it.state}`)} color={color[it.state]} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 600, fontSize: ".92rem", m: 0 }}>{it.title} <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>({it.cycle})</Box></Typography>
              <Typography sx={{ fontSize: ".82rem", color: "text.secondary", m: 0 }}>{t("admin.complianceDeadline", { date: it.deadline })} — {it.description}</Typography>
            </Box>
            {it.state !== "done" && (
              <Button size="small" variant="outlined" onClick={async () => { await AdminApi.markComplianceDone(it.id); load(); }}>{t("admin.complianceDone")}</Button>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function OrgRow({ org, onChanged, open, onToggle }: { org: AdminOrg; onChanged: () => void; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(org.name);
  const [seats, setSeats] = useState(org.seatLimit ? String(org.seatLimit) : "");
  const [allowance, setAllowance] = useState(org.fairUseCapPerMember != null ? String(org.fairUseCapPerMember) : "");
  const [kbo, setKbo] = useState(org.kboNumber || "");
  const [vat, setVat] = useState(org.vatNumber || "");
  const [contractStart, setContractStart] = useState(org.contractStartDate ? String(org.contractStartDate).slice(0, 10) : "");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const save = async () => {
    setMsg(null);
    try {
      await AdminApi.updateOrg(org.id, { name: name.trim() || undefined, seatLimit: seats === "" ? null : Number(seats), fairUseCapPerMember: allowance === "" ? undefined : Number(allowance), kboNumber: kbo, vatNumber: vat, contractStartDate: contractStart });
      setMsg({ kind: "success", text: t("admin.orgSaved") });
      onChanged();
    } catch { setMsg({ kind: "error", text: t("admin.orgAddError") }); }
  };
  const add = async () => {
    if (!email.trim()) return;
    setMsg(null);
    try {
      await AdminApi.addOrgAdmin(org.id, email.trim());
      setMsg({ kind: "success", text: t("admin.orgAdminAdded") });
      setEmail("");
      onChanged();
    } catch (e: any) {
      setMsg({ kind: "error", text: e?.response?.status === 404 ? t("admin.orgUserNotFound") : t("admin.orgAddError") });
    }
  };
  const removeAdmin = async (userId: string) => { await AdminApi.removeOrgAdmin(org.id, userId); onChanged(); };

  // Skill-principe "structuur codeert informatie": groepstitels en witruimte
  // i.p.v. geneste vlakken — de pagina heeft al twee lagen (crème + kaart).
  const groupSx = { pt: 0.5 } as const;
  const groupTitleSx = { fontSize: ".78rem", textTransform: "uppercase", letterSpacing: ".08em", color: "text.secondary", mb: 1.25 } as const;

  return (
    <Box sx={{ py: 0.5 }}>
      {/* Compacte rij: naam + kerninfo; klikken opent de editor. Dagelijks
          werkbaar: de lijst blijft scanbaar, bewerken gebeurt bewust. */}
      <Box component="button" onClick={onToggle}
        sx={{ all: "unset", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 1.5, width: "100%", px: 1.5, py: 1.25, borderRadius: "var(--radius-md)", cursor: "pointer", fontFamily: "inherit", bgcolor: open ? "var(--primary-50)" : "transparent", "&:hover": { bgcolor: open ? "var(--primary-50)" : "var(--surface-alt)" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
        <Typography sx={{ fontWeight: 600, m: 0, flex: 1, minWidth: 140 }}>{org.name}</Typography>
        {org.fundingSuspended && <Chip size="small" color="error" label={t("admin.orgSuspended")} />}
        <Chip size="small" variant="outlined" label={org.inviteCode} sx={{ fontFamily: "monospace" }} />
        <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0, whiteSpace: "nowrap" }}>{t("admin.orgMeta", { members: org.memberCount, admins: org.adminCount })}</Typography>
        <Typography sx={{ color: "text.secondary", m: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</Typography>
      </Box>

      {open && (
        <Stack spacing={2.5} sx={{ px: 1.5, py: 2.5, borderLeft: "3px solid var(--primary-100)", ml: 1.5, mt: 0.5, mb: 1 }}>
          <Box sx={groupSx}>
            <Typography sx={groupTitleSx}>{t("admin.orgGrpGeneral")}</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
              <TextField size="small" label={t("admin.orgName")} value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} sx={{ minWidth: 220 }} />
              <TextField size="small" label={t("admin.orgSeatLimit")} type="number" value={seats} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSeats(e.target.value)} sx={{ width: 140 }} />
              <TextField size="small" label={t("admin.orgAllowance")} type="number" value={allowance} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAllowance(e.target.value)} sx={{ width: 160 }} />
              <TextField size="small" label={t("admin.orgContractStart")} type="date" value={contractStart} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContractStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 180 }} helperText={t("admin.orgContractStartHint")} />
            </Stack>
          </Box>
          <Box sx={groupSx}>
            <Typography sx={groupTitleSx}>{t("admin.orgGrpBilling")}</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
              <TextField size="small" label={t("admin.orgKbo")} value={kbo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKbo(e.target.value)} sx={{ width: 190 }} helperText={t("admin.orgKboHint")} />
              <TextField size="small" label={t("admin.orgVat")} value={vat} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVat(e.target.value)} sx={{ width: 190 }} />
            </Stack>
          </Box>
          <Box sx={groupSx}>
            <Typography sx={groupTitleSx}>{t("admin.orgGrpAdmins")}</Typography>
            {org.admins.length > 0 && (
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {org.admins.map((a) => (
                  <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: ".9rem", m: 0, flex: 1 }}>{a.email}</Typography>
                    <Button size="small" color="error" onClick={() => removeAdmin(a.id)}>{t("admin.orgRemove")}</Button>
                  </Box>
                ))}
              </Stack>
            )}
            <Stack direction="row" spacing={1} sx={{ maxWidth: 440 }}>
              <TextField size="small" label={t("admin.orgAdminEmail")} value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} fullWidth />
              <Button size="small" variant="outlined" onClick={add} disabled={!email.trim()} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>{t("admin.orgAdd")}</Button>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Button size="small" variant="contained" onClick={save}>{t("dashboard.save")}</Button>
            {msg && <Alert severity={msg.kind} sx={{ py: 0 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
const STATE_COLOR = (s: string) => (s === "verified" ? "success" : s === "rejected" ? "error" : s === "needs_info" ? "warning" : s === "interview" ? "info" : "default");

const SOURCE_KEYS = ["fod_ecad", "compsy", "riziv", "beroepsvereniging", "document"] as const;
const CHECK_LINKS: { key: string; url: string }[] = [
  { key: "ecad", url: "https://apps.health.belgium.be/ecad-public-search-engine-web/" },
  { key: "compsy", url: "https://www.compsy.be/nl/register/apply-check" },
  { key: "riziv", url: "https://www.riziv.fgov.be/nl/webtoepassingen/een-zorgverlener-zoeken" },
];

function VettingGuide() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const statuses = t("admin.vettingGuide.statuses", { returnObjects: true }) as unknown as string[];
  const wugSteps = t("admin.vettingGuide.wugSteps", { returnObjects: true }) as unknown as string[];
  const ptSteps = t("admin.vettingGuide.ptSteps", { returnObjects: true }) as unknown as string[];
  const lines = (arr: string[]) => (
    <Stack spacing={0.4} sx={{ mb: 1.5 }}>
      {arr.map((s, i) => <Typography key={i} sx={{ fontSize: ".85rem", color: "text.secondary" }}>{s}</Typography>)}
    </Stack>
  );
  return (
    <Box sx={{ mb: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
      <Box role="button" onClick={() => setOpen((v) => !v)} sx={{ p: 2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 600 }}>{t("admin.vettingGuide.title")}</Typography>
        <Typography sx={{ color: "text.secondary" }}>{open ? "▲" : "▼"}</Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: ".9rem", mb: 0.5 }}>{t("admin.vettingGuide.statusesTitle")}</Typography>
          {lines(statuses)}
          <Typography sx={{ fontWeight: 600, fontSize: ".9rem", mb: 0.5 }}>{t("admin.vettingGuide.wugTitle")}</Typography>
          {lines(wugSteps)}
          <Typography sx={{ fontWeight: 600, fontSize: ".9rem", mb: 0.5 }}>{t("admin.vettingGuide.ptTitle")}</Typography>
          {lines(ptSteps)}
          <Typography sx={{ fontSize: ".82rem", color: "text.secondary", fontStyle: "italic" }}>{t("admin.vettingGuide.reverifyNote")}</Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

function PsychRow({ p, onChanged }: { p: AdminPsychologist; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState("");
  const [sources, setSources] = useState<string[]>([]);
  const [docs, setDocs] = useState<AdminDoc[] | null>(null);
  const [busy, setBusy] = useState(false);

  const toggleSource = (s: string) => setSources((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const setState = async (state: VerificationState) => {
    setBusy(true);
    try { await AdminApi.setStatus(p.id, state, { note: note.trim() || undefined, checkedSources: sources.length ? sources : undefined }); setNote(""); setSources([]); onChanged(); }
    finally { setBusy(false); }
  };
  const loadDocs = async () => { setDocs(await AdminApi.listDocuments(p.id)); };

  const isPt = p.profession === "psychotherapeut";
  const cred: [string, string | undefined][] = isPt
    ? [
        [t("vetting.association"), p.professionalAssociation ? t(`vetting.associations.${p.professionalAssociation}`) : undefined],
        [t("vetting.memberNumber"), p.associationMemberNumber],
        [t("vetting.legalBasis"), p.psychotherapyLegalBasis ? t(`vetting.legalBases.${p.psychotherapyLegalBasis}`) : undefined],
      ]
    : [
        [t("vetting.visa"), p.visaNumber],
        [t("vetting.erkenning"), p.erkenningNumber],
        [t("vetting.riziv"), p.rizivNumber],
        [t("vetting.psychologenCommissie"), p.psychologenCommissieNumber],
      ];

  return (
    <Box sx={{ py: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 600 }}>{p.displayName}</Typography>
        <Chip size="small" label={t(`admin.state.${p.verifiedState}`)} color={STATE_COLOR(p.verifiedState) as any} />
        {p.profession && <Chip size="small" variant="outlined" label={t(`vetting.professions.${p.profession}`)} />}
      </Box>
      <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>{p.email}</Typography>

      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {cred.filter(([, v]) => v).map(([label, v]) => (
          <Typography key={label} sx={{ fontSize: ".85rem", color: "text.secondary" }}>{label}: <strong>{v}</strong></Typography>
        ))}
      </Stack>

      {/* DAC7 / BTW intake (vetting view; the rijksregisternummer itself stays server-side) */}
      <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mt: 1 }}>
        {t("admin.billingLabel")}:{" "}
        {p.businessForm || p.vatStatus || p.vatNumber || p.kboNumber || p.tinProvided ? (
          <strong>
            {[
              p.businessForm ? t(`vetting.businessForms.${p.businessForm}`) : null,
              p.vatStatus ? t(`vetting.vatStatuses.${p.vatStatus}`) : null,
              p.vatNumber,
              p.kboNumber ? `KBO ${p.kboNumber}` : null,
              p.businessForm === "natural_person" ? (p.tinProvided ? t("admin.tinProvided") : t("admin.tinMissing")) : null,
              p.birthDate ? `° ${new Date(p.birthDate).toLocaleDateString(i18n.language)}` : null,
            ].filter(Boolean).join(" · ")}
          </strong>
        ) : (
          t("admin.billingEmpty")
        )}
      </Typography>

      {/* Official-register check links */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
        <Typography sx={{ fontSize: ".8rem", color: "text.secondary" }}>{t("vetting.checkLinksLabel")}</Typography>
        {CHECK_LINKS.map((l) => (
          <Box key={l.key} component="a" href={l.url} target="_blank" rel="noreferrer" sx={{ fontSize: ".8rem", color: "var(--link)" }}>{t(`vetting.checkLinks.${l.key}`)} ↗</Box>
        ))}
      </Box>
      {p.verificationCheck && (
        <Typography sx={{ fontSize: ".8rem", color: "success.main", mt: 0.75 }}>
          ✓ {t("vetting.checkedAgainst", { sources: p.verificationCheck.checkedSources.map((s) => t(`vetting.sources.${s}`)).join(", "), date: new Date(p.verificationCheck.checkedAt).toLocaleDateString(i18n.language) })}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
        <Typography sx={{ fontSize: ".8rem", color: "text.secondary" }}>{t("vetting.eligibleFunds")}:</Typography>
        {p.eligibleFundCodes.length === 0
          ? <Typography sx={{ fontSize: ".8rem", color: "text.secondary" }}>{t("vetting.noEligibleFunds")}</Typography>
          : p.eligibleFundCodes.map((c) => <Chip key={c} size="small" variant="outlined" label={t(`vetting.funds.${c}`, c.toUpperCase())} />)}
      </Box>

      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mt: 1 }}>
        {p.specializations.map((s) => <Chip key={s} size="small" variant="outlined" label={t(`topics.${s}.label`)} />)}
      </Stack>

      {/* Documents */}
      <Box sx={{ mt: 1.5 }}>
        {docs === null ? (
          <Button size="small" variant="text" onClick={loadDocs}>{t("vetting.documents")} ({p.documentCount})</Button>
        ) : docs.length === 0 ? (
          <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>{t("admin.noDocs")}</Typography>
        ) : (
          <Stack spacing={0.5}>
            {docs.map((d) => (
              <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip size="small" label={t(`vetting.docKinds.${d.kind}`)} />
                <Box component="a" href={AdminApi.documentUrl(p.id, d.id)} target="_blank" rel="noreferrer" sx={{ fontSize: ".85rem", color: "var(--link)" }}>{d.filename}</Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Review notes */}
      {p.reviewNotes.length > 0 && (
        <Stack spacing={0.25} sx={{ mt: 1.5, pl: 1.5, borderLeft: "2px solid var(--border)" }}>
          {p.reviewNotes.map((n, i) => (
            <Typography key={i} sx={{ fontSize: ".82rem", color: "text.secondary" }}>“{n.note}”</Typography>
          ))}
        </Stack>
      )}

      {/* Controls */}
      <TextField size="small" fullWidth multiline maxRows={3} placeholder={t("admin.notePlaceholder")} value={note} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)} sx={{ mt: 2, maxWidth: 520 }} />
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ fontSize: ".8rem", color: "text.secondary", mb: 0.75 }}>{t("vetting.checkedAgainstLabel")}</Typography>
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
          {SOURCE_KEYS.map((s) => (
            <Chip key={s} size="small" label={t(`vetting.sources.${s}`)} onClick={() => toggleSource(s)}
              variant={sources.includes(s) ? "filled" : "outlined"} color={sources.includes(s) ? "primary" : "default"} />
          ))}
        </Stack>
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mt: 1.5 }}>
        <Button size="small" variant="contained" color="success" onClick={() => setState("verified")} disabled={busy || p.verifiedState === "verified"}>{t("admin.verify")}</Button>
        <Button size="small" variant="outlined" onClick={() => setState("needs_info")} disabled={busy || p.verifiedState === "needs_info"}>{t("admin.needsInfo")}</Button>
        <Button size="small" variant="outlined" onClick={() => setState("interview")} disabled={busy || p.verifiedState === "interview"}>{t("admin.interview")}</Button>
        <Button size="small" variant="outlined" color="error" onClick={() => setState("rejected")} disabled={busy || p.verifiedState === "rejected"}>{t("admin.reject")}</Button>
      </Stack>
    </Box>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState<string>("overview");
  const [overview, setOverview] = useState<AdminOverviewData | null>(null);
  const [openOrgId, setOpenOrgId] = useState("");
  const [list, setList] = useState<AdminPsychologist[]>([]);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [orgName, setOrgName] = useState("");
  const [orgSeats, setOrgSeats] = useState("");
  const [orgAllowance, setOrgAllowance] = useState("");
  const [privateMode, setPrivateMode] = useState<boolean | null>(null);
  const [privateModeError, setPrivateModeError] = useState(false);

  const load = useCallback(() => {
    AdminApi.listPsychologists(filter === "all" ? undefined : filter).then(setList).catch(() => setList([]));
  }, [filter]);
  const loadOrgs = useCallback(() => { AdminApi.listOrgs().then(setOrgs).catch(() => setOrgs([])); }, []);

  useEffect(() => {
    if (user?.role !== "platform_admin") return;
    if (tab === "psychologists") load();
    else if (tab === "organisations") loadOrgs();
  }, [user, tab, load, loadOrgs]);

  // Eén overview-fetch voedt de hero én de menu-badges (skill: structuur
  // codeert informatie — badges tonen échte aantallen, geen decoratie).
  useEffect(() => {
    if (user?.role !== "platform_admin" || user.mfaRequired) return;
    AdminApi.overview().then(setOverview).catch(() => setOverview(null));
  }, [user, tab]);

  useEffect(() => {
    if (user?.role !== "platform_admin" || user.mfaRequired) return;
    AdminApi.getSettings().then((s) => setPrivateMode(s.privateMode)).catch(() => setPrivateMode(null));
  }, [user]);

  const togglePrivateMode = async (value: boolean) => {
    const prev = privateMode;
    setPrivateModeError(false);
    setPrivateMode(value); // optimistic
    try { await AdminApi.setPrivateMode(value); }
    catch { setPrivateMode(prev); setPrivateModeError(true); }
  };

  if (!user || user.role !== "platform_admin") {
    return <Container maxWidth="sm" sx={{ py: 10 }}><Typography>{t("admin.notAuthorized")}</Typography></Container>;
  }
  // Admins must have MFA — gate the whole admin panel until it's enrolled.
  if (user.mfaRequired) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: "2rem", mb: 1 }}>{t("mfa.gateTitle")}</Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>{t("mfa.gateBody")}</Typography>
        <MfaSetup />
      </Container>
    );
  }

  const createOrg = async () => {
    if (!orgName.trim()) return;
    await AdminApi.createOrg(orgName.trim(), orgSeats ? Number(orgSeats) : undefined, orgAllowance ? Number(orgAllowance) : undefined);
    setOrgName(""); setOrgSeats(""); setOrgAllowance(""); loadOrgs();
  };

  const psychologistsNode = (
    <>
      <VettingGuide />
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {(["pending", "all"] as const).map((f) => (
          <Chip key={f} label={t(`admin.filter${f === "pending" ? "Pending" : "All"}`)} onClick={() => setFilter(f)} variant={filter === f ? "filled" : "outlined"} color={filter === f ? "primary" : "default"} />
        ))}
      </Stack>
      {list.length === 0 ? (
        <Typography sx={{ color: "text.secondary" }}>{t("admin.noResults")}</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {list.map((p) => <PsychRow key={p.id} p={p} onChanged={load} />)}
        </Stack>
      )}
    </>
  );

  const organisationsNode = (
    <>
      <Box sx={{ mb: 3, pb: 3, borderBottom: "1px solid var(--border)" }}>
        <Typography sx={{ fontSize: ".78rem", textTransform: "uppercase", letterSpacing: ".08em", color: "text.secondary", mb: 1.5 }}>{t("admin.orgNewTitle")}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
          <TextField size="small" label={t("admin.orgName")} value={orgName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrgName(e.target.value)} sx={{ minWidth: 240 }} />
          <TextField size="small" label={t("admin.orgSeatLimit")} type="number" value={orgSeats} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrgSeats(e.target.value)} sx={{ width: 170 }} />
          <TextField size="small" label={t("admin.orgAllowance")} type="number" value={orgAllowance} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrgAllowance(e.target.value)} sx={{ width: 170 }} />
          <Button size="small" variant="contained" onClick={createOrg} disabled={!orgName.trim()}>{t("admin.orgCreate")}</Button>
        </Stack>
      </Box>
      {orgs.length === 0 ? (
        <Typography sx={{ color: "text.secondary" }}>{t("admin.orgNone")}</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {orgs.map((o) => <OrgRow key={o.id} org={o} onChanged={loadOrgs} open={openOrgId === o.id} onToggle={() => setOpenOrgId(openOrgId === o.id ? "" : o.id)} />)}
        </Stack>
      )}
    </>
  );

  const settingsNode = (
    <Box>
      <FormControlLabel
        control={<Switch checked={privateMode ?? true} onChange={(e: React.ChangeEvent<HTMLInputElement>) => togglePrivateMode(e.target.checked)} disabled={privateMode === null} />}
        label={t("admin.privateModeLabel")}
      />
      <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>
        {privateMode === null ? t("admin.privateModeLoading") : privateMode ? t("admin.privateModeOn") : t("admin.privateModeOff")}
      </Typography>
      {privateModeError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setPrivateModeError(false)}>{t("admin.privateModeError")}</Alert>}
    </Box>
  );

  // Zijmenu-secties (2026-07-overhaul): alles wat de admin-API kan, nu ook in
  // de UI — met het overzicht als startpunt i.p.v. een lange stapel blokken.
  const sections: { key: string; label: string; node: React.ReactNode; badge?: number }[] = [
    { key: "overview", label: t("admin.sec.overview"), node: <OverviewSection data={overview} onNavigate={setTab} /> },
    { key: "psychologists", label: t("admin.tabPsychologists"), node: psychologistsNode, badge: overview?.vetting.pending || 0 },
    { key: "organisations", label: t("admin.tabOrganisations"), node: organisationsNode },
    { key: "users", label: t("admin.sec.users"), node: <UsersSection /> },
    { key: "invoices", label: t("admin.sec.invoices"), node: <InvoicesSection />, badge: overview?.billing.openInvoices || 0 },
    { key: "settlement", label: t("admin.watchdogTitle"), node: <BillingWatchdog /> },
    { key: "pricing", label: t("admin.tabPricing"), node: <PricingCalculator /> },
    { key: "dac7", label: t("admin.sec.dac7"), node: <Dac7Section /> },
    { key: "compliance", label: t("admin.complianceTitle"), node: <ComplianceCalendar />, badge: (overview?.compliance.open || 0) + (overview?.compliance.overdue || 0) },
    { key: "automation", label: t("admin.sec.automation"), node: <AdminAutomation />, badge: overview?.compliance.overdue || 0 },
    { key: "audit", label: t("admin.sec.audit"), node: <AuditSection /> },
    { key: "settings", label: t("admin.sec.settings"), node: settingsNode },
  ];
  // Menu-groepen (skill: structuur codeert informatie): mensen / geld /
  // naleving / systeem — de volgorde van een werkdag, niet van de codebase.
  const groups: { label: string; keys: string[] }[] = [
    { label: "", keys: ["overview"] },
    { label: t("admin.grp.people"), keys: ["psychologists", "organisations", "users"] },
    { label: t("admin.grp.money"), keys: ["invoices", "settlement", "pricing"] },
    { label: t("admin.grp.compliance"), keys: ["automation", "dac7", "compliance", "audit"] },
    { label: t("admin.grp.system"), keys: ["settings"] },
  ];
  const current = sections.find((x) => x.key === tab) || sections[0];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
      <Typography variant="h1" sx={{ fontSize: { xs: "1.8rem", sm: "2.4rem" }, mb: 3 }}>{t("admin.title")}</Typography>
      <Box sx={{ display: { md: "grid" }, gridTemplateColumns: { md: "230px 1fr" }, gap: { md: 4 }, alignItems: "start" }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 0.25, overflowX: { xs: "auto", md: "visible" }, pb: { xs: 1, md: 0 }, mb: { xs: 2, md: 0 }, position: { md: "sticky" }, top: { md: 24 } }}>
          {groups.map((g, gi) => (
            <React.Fragment key={gi}>
              {g.label && (
                <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".08em", color: "text.secondary", px: 2, mt: gi === 0 ? 0 : 2, mb: 0.5 }}>
                  {g.label}
                </Typography>
              )}
              {g.keys.map((key) => {
                const x = sections.find((sx) => sx.key === key)!;
                return (
                  <Box key={x.key} component="button" onClick={() => setTab(x.key)}
                    sx={{ all: "unset", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, borderRadius: "var(--radius-md)", cursor: "pointer", whiteSpace: "nowrap", fontSize: ".95rem", fontFamily: "inherit", fontWeight: current.key === x.key ? 600 : 400, color: current.key === x.key ? "primary.dark" : "text.primary", bgcolor: current.key === x.key ? "var(--primary-50)" : "transparent", "&:hover": { bgcolor: current.key === x.key ? "var(--primary-50)" : "var(--surface-alt)" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
                    <Box component="span" sx={{ flex: 1 }}>{x.label}</Box>
                    {(x.badge || 0) > 0 && (
                      <Box component="span" sx={{ fontSize: ".72rem", fontWeight: 700, minWidth: 20, height: 20, px: 0.5, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", bgcolor: "error.main", color: "#fff" }}>
                        {x.badge}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
          <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: 2.5 }}>{current.label}</Typography>
          {current.node}
        </Box>
      </Box>
    </Container>
  );
}
